import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { getAgingReport } from '../api';
import { PurchaseOrder, AgingReport as AgingReportType } from '../types/purchaseOrder';
import { formatDate } from '../utils';

const AgingReport: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [agingData, setAgingData] = useState<AgingReportType>({
    fourPlusDays: [],
    tenPlusDays: []
  });

  useEffect(() => {
    loadAgingReport();
  }, []);

  const loadAgingReport = async () => {
    try {
      setLoading(true);
      const data = await getAgingReport();
      setAgingData(data);
    } catch (error) {
      console.error('Error loading aging report:', error);
    } finally {
      setLoading(false);
    }
  };

  const dateTemplate = (rowData: PurchaseOrder) => {
    return formatDate(rowData.createdAt);
  };

  const clientTemplate = (rowData: PurchaseOrder) => {
    return rowData.client?.name || 'N/A';
  };

  const renderTable = (data: PurchaseOrder[]) => (
    <DataTable
      value={data}
      paginator
      rows={10}
      loading={loading}
      emptyMessage="No purchase orders found"
      responsiveLayout="scroll"
      className="p-datatable-sm"
      rowHover
      showGridlines
      stripedRows
    >
      <Column 
        field="poNumber" 
        header="PO Number" 
        sortable 
        headerClassName="text-lg font-medium"
        bodyClassName="text-lg"
      />
      <Column 
        field="createdAt" 
        header="Created Date" 
        body={dateTemplate} 
        sortable 
        headerClassName="text-lg font-medium"
        bodyClassName="text-lg"
      />
      <Column 
        field="client" 
        header="Client" 
        body={clientTemplate} 
        sortable 
        headerClassName="text-lg font-medium"
        bodyClassName="text-lg"
      />
      <Column 
        field="status" 
        header="Status" 
        sortable 
        headerClassName="text-lg font-medium"
        bodyClassName="text-lg"
      />
    </DataTable>
  );

  const header = (
    <div className="flex flex-column p-4">
      <div className="text-2xl font-bold mb-3">Purchase Orders Aging Report</div>
      <div className="text-xl">
        Total Overdue POs: <span className="font-bold text-red-600">
          {agingData.fourPlusDays.length + agingData.tenPlusDays.length}
        </span>
      </div>
    </div>
  );

  return (
    <Card header={header} className="mb-6 shadow-2">
      <TabView activeIndex={0}>
        <TabPanel header="10+ Days" className="text-lg">
          <div className="text-xl font-medium mb-4 text-red-900">
            Purchase Orders Not Completed (10+ Days Old) ({agingData.tenPlusDays.length})
          </div>
          {renderTable(agingData.tenPlusDays)}
        </TabPanel>
        <TabPanel header="4+ Days" className="text-lg">
          <div className="text-xl font-medium mb-4 text-red-600">
            Purchase Orders Not Completed (4-9 Days Old) ({agingData.fourPlusDays.length})
          </div>
          {renderTable(agingData.fourPlusDays)}
        </TabPanel>
      </TabView>
    </Card>
  );
};

export default AgingReport;
